import { RefObject, useCallback, useEffect, useRef, useState } from "react"
import { scrollWindowToNestedItem } from "../../hooks/useNestedScroll"
import { UseSectorScrollSpyReturn } from "./type"

type SectorScrollSpyOptions = {
  stickyTop?: number
  mobileStickyTop?: number
  mobileMaxWidth?: number
}

export const useSectorScrollSpy = (
  sectionRef: RefObject<HTMLElement | null>,
  scrollableRef: RefObject<HTMLElement | null>,
  options: SectorScrollSpyOptions = {},
  initialId = "0"
): UseSectorScrollSpyReturn => {
  const {
    stickyTop = 106,
    mobileStickyTop = 96,
    mobileMaxWidth = 768,
  } = options

  const [activeId, setActiveId] = useState<string>(initialId)
  const [hoverId, setHoverId] = useState<string | null>(null)

  // Stores references to each sector card/item
  const sectorRefs = useRef<Record<string, HTMLElement | null>>({})

  // Guard to prevent the IntersectionObserver from updating state while
  const isInternalScrolling = useRef(false)

  const handleClick = useCallback(
    (id: string) => {
      const el = sectorRefs.current[id]
      const section = sectionRef.current
      const scrollable = scrollableRef.current

      if (!el || !section || !scrollable) return

      setActiveId(id)
      isInternalScrolling.current = true

      scrollWindowToNestedItem(section, scrollable, el, {
        stickyTop,
        mobileStickyTop,
        mobileMaxWidth,
      })

      // Reset the guard after the smooth scroll duration
      setTimeout(() => {
        isInternalScrolling.current = false
      }, 800)
    },
    [sectionRef, scrollableRef, stickyTop, mobileStickyTop, mobileMaxWidth]
  )

  useEffect(() => {
    const scrollable = scrollableRef.current
    const sectorNodes = Object.values(sectorRefs.current).filter(
      Boolean
    ) as HTMLElement[]

    if (sectorNodes.length === 0) return

    const mq = window.matchMedia(`(max-width: ${mobileMaxWidth}px)`)

    const createObserver = () => {
      // In mobile, we observe relative to the viewport (null)
      // In desktop, we observe relative to the scrollable container
      const root = mq.matches ? null : scrollable

      return new IntersectionObserver(
        (entries) => {
          if (isInternalScrolling.current) return

          entries.forEach((entry) => {
            const id = entry.target.getAttribute("data-id")
            if (entry.isIntersecting && id) {
              setActiveId(id)
            }
          })
        },
        {
          root,
          threshold: mq.matches ? 0.3 : 0.6, // Tighter threshold for desktop
          rootMargin: mq.matches ? `-${mobileStickyTop}px 0px 0px 0px` : "0px",
        }
      )
    }

    let observer = createObserver()
    sectorNodes.forEach((node) => observer.observe(node))

    const handleResize = () => {
      observer.disconnect()
      observer = createObserver()
      sectorNodes.forEach((node) => observer.observe(node))
    }

    mq.addEventListener("change", handleResize)

    return () => {
      mq.removeEventListener("change", handleResize)
      observer.disconnect()
    }
  }, [scrollableRef, mobileMaxWidth, mobileStickyTop])

  return {
    activeId,
    hoverId,
    setHoverId,
    sectorRefs,
    handleClick,
  }
}
